/**
 * Flow-config save-path normalization
 *
 * Runs on the flow_config immediately before validation + persistence so that
 * saved configs always satisfy the return app's validation contract:
 *
 *  - Language keys whose value is empty/whitespace are removed entirely
 *    (the return app treats a present-but-empty "de"/"en" as a hard error,
 *    and empty keys like `"hu": ""` pollute stored configs).
 *  - Every non-empty text object is guaranteed an "en" entry: when the
 *    merchant didn't provide one it is copied from the best available
 *    language (merchant language priority order, then any remaining key).
 *  - Optional text objects that end up with no content are omitted rather
 *    than saved as empty objects; required ones are left empty so the
 *    validator reports them.
 *  - Instruction-box location_ids are cleaned (blank entries dropped, the
 *    key omitted when no locations are selected).
 */

import type {
  FlowConfig,
  Section,
  PhotoUploadSection,
  QuestionSetSection,
  CompletionSection,
  Question,
  QuestionOption,
  InstructionBox,
  MultilingualText,
} from '../types/merchant-configurations';

/**
 * Normalize one multilingual text object.
 * Returns undefined when no language has any content.
 */
export function normalizeMultilingualText(
  text: MultilingualText | null | undefined,
  merchantLanguages: string[]
): MultilingualText | undefined {
  if (!text || typeof text !== 'object') return undefined;

  const out: MultilingualText = {};
  for (const [lang, value] of Object.entries(text)) {
    if (typeof value === 'string' && value.trim() !== '') {
      out[lang] = value;
    }
  }

  const filledLangs = Object.keys(out);
  if (filledLangs.length === 0) return undefined;

  if (!out.en) {
    const source = merchantLanguages.find((lang) => out[lang]) ?? filledLangs[0];
    out.en = out[source];
  }

  return out;
}

/**
 * Rebuild an object with the given multilingual fields either replaced by
 * their normalized value or omitted when they normalized away to nothing.
 */
function withNormalizedTexts<T extends object>(
  base: T,
  texts: Partial<Record<keyof T & string, MultilingualText | undefined>>
): T {
  const result = { ...base } as Record<string, unknown>;
  for (const [key, value] of Object.entries(texts)) {
    if (value === undefined) {
      delete result[key];
    } else {
      result[key] = value;
    }
  }
  return result as T;
}

function normalizeOption(option: QuestionOption, langs: string[]): QuestionOption {
  // label is required by the contract: keep an empty object when it has no
  // content so validation reports it instead of it silently disappearing.
  const normalized: QuestionOption = {
    ...option,
    label: normalizeMultilingualText(option.label, langs) ?? {},
  };

  const blockingMessage = normalizeMultilingualText(option.blocking_message, langs);
  if (blockingMessage === undefined) {
    delete normalized.blocking_message;
  } else {
    normalized.blocking_message = blockingMessage;
  }

  if (option.details_config) {
    const placeholder = normalizeMultilingualText(option.details_config.placeholder, langs);
    normalized.details_config = { ...option.details_config };
    if (placeholder === undefined) {
      delete normalized.details_config.placeholder;
    } else {
      normalized.details_config.placeholder = placeholder;
    }
  }

  return normalized;
}

function normalizeQuestion(question: Question, langs: string[]): Question {
  const normalized: Question = {
    ...question,
    text: normalizeMultilingualText(question.text, langs) ?? {},
  };

  const preamble = normalizeMultilingualText(question.preamble, langs);
  if (preamble === undefined) {
    delete normalized.preamble;
  } else {
    normalized.preamble = preamble;
  }

  if (question.options) {
    normalized.options = question.options.map((o) => normalizeOption(o, langs));
  }

  return normalized;
}

function normalizeInstructionBox(box: InstructionBox, langs: string[]): InstructionBox {
  const normalized: InstructionBox = {
    ...box,
    title: normalizeMultilingualText(box.title, langs) ?? {},
    text: normalizeMultilingualText(box.text, langs) ?? {},
  };

  // location_ids: drop blank entries; omit the key entirely when no
  // locations are selected so the box shows everywhere (spec default).
  const ids = (box.location_ids ?? []).filter(
    (id) => typeof id === 'string' && id.length > 0
  );
  if (ids.length === 0) {
    delete normalized.location_ids;
  } else {
    normalized.location_ids = ids;
  }

  return normalized;
}

function normalizePhotoUploadSection(section: PhotoUploadSection, langs: string[]): PhotoUploadSection {
  return {
    ...section,
    config: withNormalizedTexts(section.config, {
      header_text: normalizeMultilingualText(section.config.header_text, langs),
      instructions: normalizeMultilingualText(section.config.instructions, langs),
    }),
  };
}

function normalizeQuestionSetSection(section: QuestionSetSection, langs: string[]): QuestionSetSection {
  const config = withNormalizedTexts(section.config, {
    header_text: normalizeMultilingualText(section.config.header_text, langs),
    instructions: normalizeMultilingualText(section.config.instructions, langs),
  });

  config.questions = (section.config.questions || []).map((q) => normalizeQuestion(q, langs));

  if (section.config.item_photos_config) {
    config.item_photos_config = withNormalizedTexts(section.config.item_photos_config, {
      instructions: normalizeMultilingualText(section.config.item_photos_config.instructions, langs),
    });
  }

  return { ...section, config };
}

function normalizeCompletionSection(section: CompletionSection, langs: string[]): CompletionSection {
  const config = {
    ...section.config,
    // Required by the contract: keep empty objects so validation reports them.
    header_text: normalizeMultilingualText(section.config.header_text, langs) ?? {},
    message: normalizeMultilingualText(section.config.message, langs) ?? {},
    instruction_boxes: (section.config.instruction_boxes || []).map((b) =>
      normalizeInstructionBox(b, langs)
    ),
  };

  if (section.config.review_request) {
    config.review_request = withNormalizedTexts(section.config.review_request, {
      text: normalizeMultilingualText(section.config.review_request.text, langs),
      button_text: normalizeMultilingualText(section.config.review_request.button_text, langs),
    });
  }

  return { ...section, config };
}

function normalizeSection(section: Section, langs: string[]): Section {
  switch (section.type) {
    case 'photo_upload':
      return normalizePhotoUploadSection(section, langs);
    case 'question_set':
      return normalizeQuestionSetSection(section, langs);
    case 'completion':
      return normalizeCompletionSection(section, langs);
    default:
      return section;
  }
}

/**
 * Normalize a complete flow config for persistence.
 *
 * @param flowConfig - the config as edited in the panel
 * @param merchantLanguages - the merchant's supported languages, in priority
 *   order (used to pick the source language when auto-filling "en")
 */
export function normalizeFlowConfig(flowConfig: FlowConfig, merchantLanguages: string[]): FlowConfig {
  return {
    ...flowConfig,
    sections: (flowConfig.sections || []).map((s) => normalizeSection(s, merchantLanguages)),
  };
}
